import { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../common/Hooks";
import PubSub from "../../services/PubSub";
import Product from "../../services/product";
import Utils from "../../utils/utils";
import CreateProduct from "./CreateProduct";
import CreateProductVersion from "./CreateProductVersion";
import ManageProducts from "./ManageProducts";
import MigrateProducts from "./MigrateProducts";
import { Accordion } from "react-bootstrap";
import AccordionItem from "../controls/AccordionItem";
import Toolbar from "../controls/Toolbar";

const ProductsList = () => {
    const [translate] = useTranslation();
    const history = useHistory();
    const [products, setProducts] = useState([]);
    const [showCreateProductModal, setShowCreateProductModal] = useState(false);
    const [showCreateProductVersionModal, setShowCreateProductVersionModal] = useState(false);
    const [showProductUpdateModal, setShowProductUpdateModal] = useState(false);
    const [showMigrateProductsModal, setShowMigrateProductsModal] = useState(false);

    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "domain"
    });

    const getAllProducts = useCallback(async () => {
        const response = await Product.getAllProductVersions(Utils.getDomain());
        if (!response.error) {
            const updatedData = Object.keys(response.data).map((key) => {
                return {
                    _id: response.data[key].length > 0
                        ? response.data[key][0].product._id
                        : null,
                    helpUrl: response.data[key].length > 0
                        ? response.data[key][0].product.helpUrl
                        : null,
                    termsAndConditionsUrl: response.data[key].length > 0
                        ? response.data[key][0].product.termsAndConditionsUrl
                        : null,
                    authProvider: response.data[key].length > 0
                        ? response.data[key][0].product.authProvider
                        : null,
                    authProviderConfig: response.data[key].length > 0
                        ? response.data[key][0].product.authProviderConfig
                        : null,
                    name: key,
                    version: response.data[key].map((el) => {
                        return { ...el, name: el.productVersion };
                    })
                };
            });
            setProducts(updatedData);
        }
    }, []);

    useEffect(() => {
        getAllProducts();
    }, [getAllProducts]);

    useEffect(() => {
        const subscriptionId = PubSub.subscribe("refresh-product-list", () => {
            getAllProducts();
        });

        return () => PubSub.unsubscribe("refresh-product-list", subscriptionId);
    }, [getAllProducts]);

    const showProductSettingsGroup = useCallback((_, productVersionId) => {
        history.push(`/products/${productVersionId}`);
    }, [history]);

    const renderAccordion = useMemo(() => {
        return products.map((el, index) => (
            <AccordionItem
                heading={el.name}
                content={el.version}
                key={index}
                elementId={el._id}
                onHeaderClick={null}
                onItemClick={showProductSettingsGroup}
                noItemMessage="OTM_NO_PRODUCT_VERSIONS"
            />
        ));
    }, [products, showProductSettingsGroup]);

    const renderToolbar = useMemo(() => {
        return (
            <Toolbar
                buttons={[
                    {
                        name: "Sync",
                        title: translate("OTM_MIGRATE_PRODUCTS"),
                        onClick: () => setShowMigrateProductsModal(true),
                        disabled: !permissions.create
                    },
                    {
                        name: "Edit",
                        title: translate("OTM_UPDATE_PRODUCT"),
                        onClick: () => setShowProductUpdateModal(true),
                        disabled: !permissions.update
                    },
                    {
                        name: "Add",
                        title: translate("OTM_CREATE_PRODUCT"),
                        onClick: () => setShowCreateProductModal(true),
                        disabled: !permissions.create
                    },
                    {
                        name: "PlaylistAdd",
                        title: translate("OTM_CREATE_PRODUCT_VERSION"),
                        onClick: () => setShowCreateProductVersionModal(true),
                        disabled: !permissions.create || products.length === 0
                    }
                ]}
                heading={translate("OTM_PRODUCTS")}
            />
        );
    }, [translate, permissions, products]);

    return (
        <>
            <ManageProducts
                show={showProductUpdateModal}
                onCancel={() => setShowProductUpdateModal(false)}
                products={products}
            />
            <CreateProduct
                show={showCreateProductModal}
                onCancel={() => setShowCreateProductModal(false)}
            />
            <CreateProductVersion
                show={showCreateProductVersionModal}
                products={products}
                onCancel={() => setShowCreateProductVersionModal(false)}
            />
            <MigrateProducts
                show={showMigrateProductsModal}
                onCancel={() => setShowMigrateProductsModal(false)} />
            <div className="card">
                <div className="card-header border-bottom">
                    {renderToolbar}
                </div>
                <div className="card-body">
                    <Accordion>
                        {renderAccordion}
                    </Accordion>
                </div>
            </div>
        </>
    );
};

export default ProductsList;